import { useMemo, useState } from "react";
import { RefreshCw } from "lucide-react";
import { useBuckets } from "./shared/useBuckets";
import { SettlementDrawer } from "./shared/SettlementDrawer";
import {
  ApplicationsTable,
  type Column,
} from "../applications/shared/ApplicationsTable";
import { StatusBadge } from "../applications/shared/StatusBadge";
import { formatApplicantName } from "../applications/shared/format";
import { LoadingBlock, ErrorBlock } from "../dashboard/shared/StateBlocks";
import { fmtCLP } from "../dashboard/format";
import {
  SETTLEMENT_STATUS_LABELS,
  formatDateShort,
  settlementStatusTone,
} from "./shared/labels";
import type { HistoryRow, InTransferRow, SettlementStatus } from "./types";

type HistoryItem = (HistoryRow | InTransferRow) & { id: number; resolvedAt: string | null };

export function HistoryScreen() {
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [filter, setFilter] = useState<"all" | "transferred" | "failed" | "in_transfer">("all");
  const query = useBuckets();

  const items: HistoryItem[] = useMemo(() => {
    const en = query.data?.buckets.enTransferencia ?? [];
    const hist = query.data?.buckets.historico ?? [];
    const combined: HistoryItem[] = [
      ...en.map((r) => ({
        ...r,
        id: r.settlementId,
        resolvedAt: r.transferStartedAt,
      })),
      ...hist.map((r) => ({
        ...r,
        id: r.settlementId,
        resolvedAt: r.transferredAt || r.transferFailedAt,
      })),
    ];

    let filtered = combined;
    if (filter === "transferred") filtered = combined.filter((r) => r.status === "TRANSFERIDA");
    else if (filter === "failed") filtered = combined.filter((r) => r.status === "TRANSFERENCIA_FALLIDA");
    else if (filter === "in_transfer")
      filtered = combined.filter((r) => r.status === "TRANSFERENCIA_PENDIENTE");

    return filtered.sort(
      (a, b) =>
        new Date(b.resolvedAt || 0).getTime() - new Date(a.resolvedAt || 0).getTime(),
    );
  }, [query.data, filter]);

  return (
    <div className="flex flex-col gap-4 animate-fade-in">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="nx-eyebrow mb-1">Liquidaciones · Histórico</p>
          <h1 className="nx-h1">Histórico de transferencias</h1>
          <p className="text-sm text-fg-muted mt-1">
            Liquidaciones marcadas como pendiente de transferencia, transferidas
            o fallidas. Click en una fila para ver el comprobante adjunto y el
            audit log completo.
          </p>
        </div>
        <button
          onClick={() => query.refetch()}
          disabled={query.isFetching}
          className="nx-btn-secondary"
        >
          <RefreshCw size={14} className={query.isFetching ? "animate-spin" : ""} />
          Actualizar
        </button>
      </header>

      <div className="inline-flex bg-bg-subtle border border-border rounded-md p-0.5 self-start">
        {(
          [
            { value: "all", label: "Todo" },
            { value: "in_transfer", label: "En transferencia" },
            { value: "transferred", label: "Transferidas" },
            { value: "failed", label: "Fallidas" },
          ] as const
        ).map((opt) => (
          <button
            key={opt.value}
            onClick={() => setFilter(opt.value)}
            className={`px-3 py-1.5 text-xs font-medium rounded-sm transition-colors ${
              filter === opt.value
                ? "bg-bg-card text-fg shadow-nx-sm"
                : "text-fg-muted hover:text-fg"
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>

      {query.isLoading ? (
        <LoadingBlock label="Cargando histórico…" />
      ) : query.isError ? (
        <ErrorBlock onRetry={() => query.refetch()} />
      ) : (
        <ApplicationsTable<HistoryItem>
          rows={items}
          columns={columns}
          page={1}
          pageSize={items.length || 1}
          total={items.length}
          onPageChange={() => {}}
          onRowClick={(r) => setSelectedId(r.settlementId)}
          selectedId={selectedId}
          emptyLabel="Sin entradas para el filtro actual."
        />
      )}

      <SettlementDrawer settlementId={selectedId} onClose={() => setSelectedId(null)} />
    </div>
  );
}

const columns: Column<HistoryItem>[] = [
  {
    key: "id",
    header: "ID",
    cell: (r) => <span className="text-fg-subtle tabular-nums">#{r.settlementId}</span>,
    width: "min",
  },
  {
    key: "event",
    header: "Evento",
    cell: (r) => (
      <div>
        <div className="font-medium text-fg truncate max-w-xs">{r.eventName}</div>
        <div className="text-2xs text-fg-subtle">{formatDateShort(r.eventDate)}</div>
      </div>
    ),
  },
  {
    key: "productor",
    header: "Productor",
    cell: (r) => (
      <div>
        <div className="text-xs text-fg">
          {formatApplicantName({ firstName: r.productorNombre })}
        </div>
        <div className="text-2xs text-fg-subtle break-all">{r.productorEmail ?? "—"}</div>
      </div>
    ),
  },
  {
    key: "amount",
    header: "Monto",
    cell: (r) => <span className="text-fg font-semibold tabular-nums">{fmtCLP(r.amountToTransfer)}</span>,
    align: "right",
    width: "min",
  },
  {
    key: "status",
    header: "Estado",
    cell: (r) => (
      <StatusBadge
        status={r.status}
        tone={settlementStatusTone(r.status as SettlementStatus)}
        label={SETTLEMENT_STATUS_LABELS[r.status as SettlementStatus]}
      />
    ),
    width: "min",
  },
  {
    key: "resolvedAt",
    header: "Cuándo",
    cell: (r) => (
      <span className="text-fg-subtle text-xs">{formatDateShort(r.resolvedAt)}</span>
    ),
    width: "min",
    align: "right",
  },
];
